// ============================================================================
// GERADOR DE "TREINO RÁPIDO" — duplicado em 16/09/2026 direto de
// fitclutch-mobile/src/config/quickWorkoutGenerator.js (motor real, mesmo
// usado no app pra montar o "Dia 1" de exemplo na tela de resultado do
// quiz). Lógica idêntica ao mobile — só os imports mudam (equipmentIcons.js
// e workoutCatalog.js viraram equipmentSlugMap.js e fullBodyPools.js aqui,
// porque esses dois arquivos do mobile dependem do sistema de i18n do app
// nativo, que a versão web não tem).
// ============================================================================

import { EXERCISE_CATALOG, MUSCLE_GROUP_KEYS } from './exercisesConfig.js';
import { MOCK_SLUG_TO_EQUIPMENT_ICON } from './equipmentSlugMap.js';
import { FULL_BODY_HOME_SLUGS, FULL_BODY_GYM_SLUGS } from './fullBodyPools.js';

export { MUSCLE_GROUP_KEYS };

export const DURATION_PRESETS_MIN = [30, 40, 50, 60];
export const QUICK_PLACE_KEYS = ['large', 'small', 'home', 'bodyweight', 'custom'];
export const CUSTOM_EQUIPMENT_KEYS = [
  'bodyweight', 'dumbbell', 'barbell', 'cable', 'leverage_machine',
  'sled_machine', 'resistance_band', 'weighted', 'suspension',
];

function getEquipment(slug) {
  return MOCK_SLUG_TO_EQUIPMENT_ICON[slug] ?? null;
}

function primaryGroupsInCatalog() {
  const byGroup = {};
  for (const key of MUSCLE_GROUP_KEYS) byGroup[key] = [];
  for (const ex of Object.values(EXERCISE_CATALOG)) {
    if (ex.muscleGroup && byGroup[ex.muscleGroup]) byGroup[ex.muscleGroup].push(ex.slug);
  }
  return byGroup;
}
const CATALOG_BY_GROUP = primaryGroupsInCatalog();

const CURATED_ORDER = {
  chest: ['push-up-standard', 'bench-press-dumbbell', 'incline-dumbbell-fly', 'chest-dip-assisted', 'barbell-bench-press', 'incline-barbell-bench-press'],
  back: ['lat-pulldown', 'seated-cable-row', 'bent-over-row-dumbbell', 'bent-over-row-barbell', 'dumbbell-deadlift', 'pull-up'],
  legs: ['leg-press-45', 'calf-raise-standing', 'bodyweight-lunge', 'stiff-leg-deadlift', 'barbell-squat'],
  shoulders: ['face-pull-cable', 'lateral-raise-dumbbell', 'front-raise-shoulder', 'seated-shoulder-press', 'military-press-barbell'],
  triceps: ['triceps-pushdown-cable', 'triceps-kickback-dumbbell', 'skull-crusher-dumbbell', 'ring-dip', 'close-grip-bench-press'],
  biceps: ['dumbbell-curl-standing', 'hammer-curl', 'concentration-curl', 'seated-barbell-curl', 'barbell-curl'],
  abs: ['crunch-basic', 'front-plank', 'russian-twist', 'bicycle-crunch', 'side-plank'],
  glutes: ['glute-bridge', 'glute-bridge-band', 'glute-bridge-single-leg', 'hip-thrust-barbell'],
  forearms: ['hand-gripper', 'weighted-hand-grip', 'reverse-wrist-curl-dumbbell', 'wrist-curl-barbell'],
};

export const MUSCLE_FOCUS_POOL = MUSCLE_GROUP_KEYS.reduce((acc, group) => {
  const real = new Set(CATALOG_BY_GROUP[group] ?? []);
  const curated = (CURATED_ORDER[group] ?? []).filter((slug) => real.has(slug));
  const leftover = (CATALOG_BY_GROUP[group] ?? []).filter((slug) => !curated.includes(slug));
  acc[group] = [...curated, ...leftover];
  return acc;
}, {});

const MINUTES_PER_EXERCISE = 7;

const LEVEL_COUNT_BOUNDS = {
  beginner: { min: 2, max: 5 },
  intermediate: { min: 3, max: 7 },
  advanced: { min: 4, max: 8 },
  expert: { min: 5, max: 10 },
};

const LEVEL_DEPTH_FRACTION = { beginner: 0.5, intermediate: 0.7, advanced: 0.9, expert: 1 };

function allowedEquipmentSet(placeKey, customEquipment) {
  if (placeKey === 'custom') {
    return customEquipment && customEquipment.length ? new Set(customEquipment) : null;
  }
  if (placeKey === 'bodyweight') return new Set(['bodyweight']);
  if (placeKey === 'home') return new Set(['bodyweight', 'dumbbell', 'resistance_band', 'weighted']);
  return null;
}

function buildFilteredPool(pool, equipmentAllowed, depthFraction) {
  const byEquipment = equipmentAllowed ? pool.filter((slug) => equipmentAllowed.has(getEquipment(slug))) : pool;
  if (!byEquipment.length) return [];
  const depthCut = Math.max(1, Math.ceil(byEquipment.length * depthFraction));
  const byDepth = byEquipment.slice(0, depthCut);
  return byDepth.length ? byDepth : byEquipment;
}

export function generateQuickWorkoutSlugs({
  durationMinutes = 30,
  levelKey = 'intermediate',
  placeKey = 'large',
  customEquipment = [],
  muscleGroups = [],
  fullBody = true,
  poolOffset = 0,
}) {
  const bounds = LEVEL_COUNT_BOUNDS[levelKey] ?? LEVEL_COUNT_BOUNDS.intermediate;
  const rawCount = Math.round(durationMinutes / MINUTES_PER_EXERCISE);
  const targetCount = Math.min(bounds.max, Math.max(bounds.min, rawCount));

  const equipmentAllowed = allowedEquipmentSet(placeKey, customEquipment);
  const depthFraction = LEVEL_DEPTH_FRACTION[levelKey] ?? 1;

  const groups = !fullBody && muscleGroups.length ? muscleGroups : null;
  let rawPools;
  if (!groups) {
    const homeBased = placeKey === 'home' || placeKey === 'bodyweight';
    rawPools = [homeBased ? FULL_BODY_HOME_SLUGS : FULL_BODY_GYM_SLUGS];
  } else {
    rawPools = groups.map((g) => MUSCLE_FOCUS_POOL[g] ?? []);
  }

  const filteredPools = rawPools.map((pool) => buildFilteredPool(pool, equipmentAllowed, depthFraction));

  const seen = new Set();
  const result = [];
  let idx = 0;
  while (result.length < targetCount) {
    let addedThisRound = false;
    for (const pool of filteredPools) {
      if (result.length >= targetCount) break;
      const slug = pool.length ? pool[(idx + poolOffset) % pool.length] : undefined;
      if (slug && !seen.has(slug)) {
        seen.add(slug);
        result.push(slug);
        addedThisRound = true;
      }
    }
    idx++;
    if (!addedThisRound) break;
  }

  return { slugs: result, estimatedMinutes: result.length * MINUTES_PER_EXERCISE };
}
